import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogType,
    PrimaryButton,
    DefaultButton,
    IconButton,
    Stack,
    TextField,
    Dropdown,
    IDropdownOption,
    ShimmeredDetailsList,
    SelectionMode,
    IColumn,
    MessageBar,
    MessageBarType,
    Spinner,
    SpinnerSize,
    TagPicker,
    NormalPeoplePicker,
    DatePicker,
    ComboBox,
    Icon
} from '@fluentui/react';
import Text from '../../../../../typography/text';
import Label from '../../../../../typography/label';
import FileService from '../../../../../../services/general/file';
import OpenAIService from '../../../../../../services/systems/openai';
import GeneralService from '../../../../../../services/general';
import ErrorService from '../../../../../../services/general/error';
import CompanyService from '../../../../../../services/users/company';
import WarehouseService from '../../../../../../services/warehouses';
import QuantityUnitService from '../../../../../../services/general/quantityUnit';
import WeightTypes from '../../../../../../manifests/weightTypes';
import { IGoodsResourceShortProps } from '../../../../../../props/goods/goods';
import { ICompanyResourceShort } from '../../../../../../props/users/company';
import { IUserResourceShortProps } from '../../../../../../props/users/user';
import { IWarehouseResourceShortProps } from '../../../../../../props/warehouses/warehouse';
import { IQuantityUnitResourceShortProps } from '../../../../../../props/general/quantityUnit';
import { ExtractedTallyItem } from './TallySheetScanModal';
import moment from 'moment';

export interface ExtractedSuratJalanInfo {
    deliveryNoteNumber?: string;
    date?: string;
    sender?: {
        companyName?: string;
        contactPerson?: string;
        address?: string;
        city?: string;
    };
    recipient?: {
        companyName?: string;
        contactPerson?: string;
        address?: string;
        city?: string;
    };
    vehicle?: {
        licensePlate?: string;
        driverName?: string;
    };
    notes?: string;
}

interface SuratJalanGoodsScanModalProps {
    isOpen: boolean;
    onDismiss(): void;
    onApply(items: ExtractedTallyItem[], suratJalanInfo?: ExtractedSuratJalanInfo): void;
    defaultCompany?: ICompanyResourceShort;
    defaultCustomer?: IUserResourceShortProps;
    defaultWarehouse?: IWarehouseResourceShortProps;
}

const MODEL_OPTIONS: IDropdownOption[] = [
    { key: 'google-ai-studio', text: 'Google AI Studio Gemini Flash (Super Cepat ~1 Detik)' },
    { key: 'xiaomi/mimo-v2.5', text: 'Xiaomi MiMo Vision 2.5 (Multimodal Vision)' },
    { key: 'deepseek/deepseek-v4-flash-0731', text: 'DeepSeek V4 Flash (OpenRouter)' },
];

const DIMENSION_UNITS = [
    { key: 'cm', text: 'cm' },
    { key: 'm', text: 'm' },
    { key: 'mm', text: 'mm' }
];

const SuratJalanGoodsScanModal: React.FC<SuratJalanGoodsScanModalProps> = (props: SuratJalanGoodsScanModalProps) => {
    const { isOpen, onDismiss, onApply, defaultCompany, defaultCustomer, defaultWarehouse } = props;

    const [file, setFile] = useState<File | undefined>();
    const [previewImages, setPreviewImages] = useState<string[]>([]);
    const [selectedModel, setSelectedModel] = useState<string>('google-ai-studio');
    const [extracting, setExtracting] = useState<boolean>(false);
    const [extractedItems, setExtractedItems] = useState<ExtractedTallyItem[]>([]);
    const [suratJalanInfo, setSuratJalanInfo] = useState<ExtractedSuratJalanInfo | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | undefined>();
    const [expandedItemId, setExpandedItemId] = useState<string | undefined>();

    const [quantityUnits, setQuantityUnits] = useState<IQuantityUnitResourceShortProps[]>([]);

    useEffect(() => {
        if (isOpen) {
            loadQuantityUnits();
        }
    }, [isOpen]);

    const loadQuantityUnits = async () => {
        try {
            const units = await QuantityUnitService.retrieve();
            setQuantityUnits(units);
        } catch (e) {
            console.error("Load quantity units error:", e);
        }
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0) return;
        const selectedFile = e.target.files[0];
        setFile(selectedFile);
        setErrorMessage(undefined);

        try {
            const base64List = await FileService.getBase64(selectedFile);
            setPreviewImages(base64List);
        } catch (e) {
            console.error(e);
            setErrorMessage("Gagal membaca preview gambar: " + ErrorService.getMessage(e));
        }
    };

    const handleProcessOCR = async () => {
        if (!file || previewImages.length === 0) return;

        setExtracting(true);
        setErrorMessage(undefined);
        setExtractedItems([]);
        setExpandedItemId(undefined);

        try {
            const fd = new FormData();
            fd.append('type', 'suratJalan');
            fd.append('model', selectedModel);
            previewImages.forEach((b64) => fd.append('base64[]', b64));

            const response = await OpenAIService.extractDocument(fd);

            let resData = response?.data || response;
            if (typeof resData === 'string') {
                try {
                    resData = JSON.parse(resData);
                } catch (e) {
                    console.error("JSON parse error:", e);
                }
            }

            const items: any[] = resData?.items || (Array.isArray(resData) ? resData : []);
            
            const parsedInfo: ExtractedSuratJalanInfo = {
                deliveryNoteNumber: resData?.deliveryNoteNumber || '',
                date: resData?.date || '',
                sender: resData?.sender || {},
                recipient: resData?.recipient || {},
                vehicle: resData?.vehicle || {},
                notes: resData?.notes || ''
            };
            setSuratJalanInfo(parsedInfo);

            if (!items || items.length === 0) {
                setErrorMessage("AI tidak menemukan daftar barang dari foto Surat Jalan ini. Mohon pastikan foto cukup jelas dan memuat tabel barang.");
                setExtracting(false);
                return;
            }

            // Determine default supplier from Surat Jalan sender (shipper)
            const defaultSupplierName = parsedInfo.sender?.companyName || parsedInfo.sender?.contactPerson || '';

            // Parse date from Surat Jalan (e.g. '14-09-2026', '14/09/2026', '2026-09-14')
            let parsedDeliveryDate: string | undefined = undefined;
            if (parsedInfo.date && parsedInfo.date.trim() !== '') {
                const cleanedDate = parsedInfo.date.trim();
                const parsed = moment(cleanedDate, ['D.M.YY', 'D.M.YYYY', 'D-M-YY', 'D-M-YYYY', 'D/M/YY', 'D/M/YYYY', 'YYYY-MM-DD', 'YYYY/MM/DD'], true);
                if (parsed.isValid()) {
                    parsedDeliveryDate = parsed.toISOString();
                } else {
                    const fallbackParsed = moment(cleanedDate);
                    if (fallbackParsed.isValid()) {
                        parsedDeliveryDate = fallbackParsed.toISOString();
                    }
                }
            }

            const formattedItems: ExtractedTallyItem[] = items.map((it: any, idx: number) => {
                return {
                    id: GeneralService.guid(),
                    itemNumber: it.itemNumber || idx + 1,
                    jenisBarang: it.goodsName || it.jenisBarang || it.name || it.description || '',
                    quantityAmount: it.quantity ? String(it.quantity) : (it.quantityAmount ? String(it.quantityAmount) : '1'),
                    quantityUnit: it.unit || it.quantityUnit || 'CTN',
                    weightAmount: it.weight ? String(it.weight) : (it.weightAmount ? String(it.weightAmount) : ''),
                    weightUnit: it.weightUnit || 'kg',
                    dimensionUnit: 'cm',
                    supplier: defaultSupplierName,
                    company: defaultCompany,
                    customer: defaultCustomer,
                    warehouse: defaultWarehouse,
                    deliveryDate: parsedDeliveryDate,
                    notes: it.notes || '',
                    matchingStatus: 'new'
                };
            });

            setExtractedItems(formattedItems);
            setExtracting(false);
        } catch (e) {
            console.error("Surat Jalan AI extraction error:", e);
            setErrorMessage("Ekstraksi Surat Jalan gagal: " + ErrorService.getMessage(e));
            setExtracting(false);
        }
    };

    const handleItemChange = (id: string, field: keyof ExtractedTallyItem, val: any) => {
        setExtractedItems((prev) =>
            prev.map((it) => (it.id === id ? { ...it, [field]: val } : it))
        );
    };

    const handleDeleteItem = (id: string) => {
        setExtractedItems((prev) => {
            const filtered = prev.filter((it) => it.id !== id);
            return filtered.map((it, idx) => ({ ...it, itemNumber: idx + 1 }));
        });
        if (expandedItemId === id) {
            setExpandedItemId(undefined);
        }
    };

    const handleAddBlankRow = () => {
        const newItem: ExtractedTallyItem = {
            id: GeneralService.guid(),
            itemNumber: extractedItems.length + 1,
            jenisBarang: '',
            quantityAmount: '1',
            quantityUnit: 'CTN',
            weightAmount: '',
            weightUnit: 'kg',
            dimensionUnit: 'cm',
            supplier: suratJalanInfo?.sender?.companyName || '',
            company: defaultCompany,
            customer: defaultCustomer,
            warehouse: defaultWarehouse,
            matchingStatus: 'new'
        };
        setExtractedItems((prev) => [...prev, newItem]);
        setExpandedItemId(newItem.id);
    };

    const handleApplyAll = () => {
        const validItems = extractedItems.filter((it) => it.jenisBarang && it.jenisBarang.trim() !== '');
        if (validItems.length === 0) {
            setErrorMessage("Tidak ada barang yang valid untuk diterapkan. Mohon isi minimal satu nama barang.");
            return;
        }
        onApply(validItems, suratJalanInfo || undefined);
        handleReset();
        onDismiss();
    };

    const handleReset = () => {
        setFile(undefined);
        setPreviewImages([]);
        setExtracting(false);
        setExtractedItems([]);
        setSuratJalanInfo(null);
        setErrorMessage(undefined);
        setExpandedItemId(undefined);
    };

    const activeItem = extractedItems.find((it) => it.id === expandedItemId);

    const _onSearchCompanies = async (keyword: string) => {
        try {
            const qs: string[] = [];
            if (keyword && keyword.trim() !== '') { qs.push(`search=${encodeURIComponent(keyword.trim())}`); }
            const res = await CompanyService.retrieve(qs.join("&"));
            const list = res?.data || (Array.isArray(res) ? res : []);
            return list.map((item: any) => ({
                key: item.id,
                name: item.name,
                metadata: item
            }));
        } catch (error) {
            console.error("Search companies error:", error);
            return [];
        }
    };

    const _onSearchCustomers = async (companyId?: string, keyword: string = '') => {
        try {
            if (!companyId) return [];
            keyword = keyword.toLowerCase();
            const users = await CompanyService.getUsers(companyId);
            return users.filter((user: any) => {
                return (user.email || '').toLowerCase().indexOf(keyword) > -1 ||
                    (user.name || '').toLowerCase().indexOf(keyword) > -1 ||
                    (user.phoneNumber || "").toLowerCase().indexOf(keyword) > -1;
            }).map((user: any) => ({
                id: user.id,
                text: user.name,
                secondaryText: user.email,
                imageInitials: GeneralService.getInitial(user.name),
                metadata: user,
            }));
        } catch (error) {
            console.error("Search customers error:", error);
            return [];
        }
    };

    const _onSearchWarehouse = async (keyword: string) => {
        try {
            const qs: string[] = [];
            if (keyword && keyword.trim() !== '') { qs.push(`search=${encodeURIComponent(keyword.trim())}`); }
            const res = await WarehouseService.retrieve.get(qs.join("&"));
            const list = Array.isArray(res) ? res : ((res as any)?.data || []);
            return list.map((item: any) => ({
                key: item.id,
                name: item.name,
                metadata: item
            }));
        } catch (error) {
            console.error("Search warehouse error:", error);
            return [];
        }
    };

    const columns: IColumn[] = [
        {
            key: 'no',
            name: 'No.',
            minWidth: 30,
            maxWidth: 35,
            onRender: (item: ExtractedTallyItem, idx?: number) => (
                <Stack styles={{ root: { paddingTop: 6 } }}>
                    <Text size="small">{(idx || 0) + 1}</Text>
                </Stack>
            )
        },
        {
            key: 'jenisBarang',
            name: 'Nama / Jenis Barang',
            minWidth: 170,
            onRender: (item: ExtractedTallyItem) => {
                const hasDetails = item.company || item.customer || item.length || item.supplier || item.warehouse;
                return (
                    <Stack tokens={{ childrenGap: 4 }} styles={{ root: { padding: '4px 0' } }}>
                        <TextField
                            value={item.jenisBarang}
                            onChange={(e, val) => handleItemChange(item.id, 'jenisBarang', val || '')}
                            disabled={extracting}
                            placeholder="Nama / Jenis Barang"
                        />
                        {hasDetails && (
                            <Stack horizontal wrap tokens={{ childrenGap: 6 }} verticalAlign="center">
                                {item.company && (
                                    <span style={{ fontSize: 10, background: '#e0f2fe', color: '#0369a1', padding: '2px 6px', borderRadius: 4, fontWeight: 500 }}>
                                        🏢 {item.company.name}
                                    </span>
                                )}
                                {item.customer && (
                                    <span style={{ fontSize: 10, background: '#fef3c7', color: '#b45309', padding: '2px 6px', borderRadius: 4, fontWeight: 500 }}>
                                        👤 {item.customer.name}
                                    </span>
                                )}
                                {item.warehouse && (
                                    <span style={{ fontSize: 10, background: '#dcfce7', color: '#15803d', padding: '2px 6px', borderRadius: 4, fontWeight: 500 }}>
                                        🏭 {item.warehouse.name}
                                    </span>
                                )}
                                {(item.length || item.width || item.height) && (
                                    <span style={{ fontSize: 10, background: '#f1f5f9', color: '#334155', padding: '2px 6px', borderRadius: 4, fontWeight: 500 }}>
                                        📐 {item.length || 0}x{item.width || 0}x{item.height || 0} {item.dimensionUnit || 'cm'} ({GeneralService.calculateVolume(item.length, item.width, item.height, item.dimensionUnit || 'cm')} m3)
                                    </span>
                                )}
                                {item.supplier && (
                                    <span style={{ fontSize: 10, background: '#f3e8ff', color: '#6b21a8', padding: '2px 6px', borderRadius: 4, fontWeight: 500 }}>
                                        📦 {item.supplier}
                                    </span>
                                )}
                            </Stack>
                        )}
                    </Stack>
                );
            }
        },
        {
            key: 'quantityAmount',
            name: 'Qty',
            minWidth: 60,
            maxWidth: 80,
            onRender: (item: ExtractedTallyItem) => (
                <Stack styles={{ root: { padding: '4px 0' } }}>
                    <TextField
                        value={item.quantityAmount}
                        onChange={(e, val) => handleItemChange(item.id, 'quantityAmount', val || '')}
                        disabled={extracting}
                    />
                </Stack>
            )
        },
        {
            key: 'quantityUnit',
            name: 'Satuan',
            minWidth: 80,
            maxWidth: 100,
            onRender: (item: ExtractedTallyItem) => (
                <Stack styles={{ root: { padding: '4px 0' } }}>
                    <TextField
                        value={item.quantityUnit}
                        onChange={(e, val) => handleItemChange(item.id, 'quantityUnit', val || '')}
                        disabled={extracting}
                        placeholder="Satuan"
                    />
                </Stack>
            )
        },
        {
            key: 'weightAmount',
            name: 'Berat (kg)',
            minWidth: 70,
            maxWidth: 85,
            onRender: (item: ExtractedTallyItem) => (
                <Stack styles={{ root: { padding: '4px 0' } }}>
                    <TextField
                        value={item.weightAmount}
                        onChange={(e, val) => handleItemChange(item.id, 'weightAmount', val || '')}
                        disabled={extracting}
                        placeholder="kg"
                    />
                </Stack>
            )
        },
        {
            key: 'supplier',
            name: 'Supplier',
            minWidth: 100,
            maxWidth: 130,
            onRender: (item: ExtractedTallyItem) => (
                <Stack styles={{ root: { padding: '4px 0' } }}>
                    <TextField
                        value={item.supplier || ''}
                        onChange={(e, val) => handleItemChange(item.id, 'supplier', val || '')}
                        disabled={extracting}
                        placeholder="Nama Supplier"
                    />
                </Stack>
            )
        },
        {
            key: 'actions',
            name: '',
            minWidth: 125,
            maxWidth: 135,
            onRender: (item: ExtractedTallyItem) => {
                const isExpanded = expandedItemId === item.id;
                return (
                    <Stack horizontal tokens={{ childrenGap: 6 }} verticalAlign="center" horizontalAlign="end" styles={{ root: { padding: '4px 2px 4px 0' } }}>
                        <DefaultButton
                            text={isExpanded ? 'Tutup' : 'Form'}
                            iconProps={{ iconName: isExpanded ? 'ChevronUp' : 'Edit' }}
                            styles={{
                                root: {
                                    height: 28,
                                    padding: '0 8px',
                                    fontSize: 11,
                                    borderRadius: 4,
                                    borderColor: isExpanded ? '#0284c7' : '#cbd5e1',
                                    color: isExpanded ? '#0284c7' : '#475569',
                                    backgroundColor: isExpanded ? '#f0f9ff' : '#ffffff'
                                }
                            }}
                            onClick={() => setExpandedItemId(isExpanded ? undefined : item.id)}
                        />
                        <IconButton
                            iconProps={{ iconName: 'Delete' }}
                            title="Hapus baris ini"
                            styles={{
                                root: { height: 28, width: 28, color: '#ef4444', borderRadius: 4 },
                                rootHovered: { backgroundColor: '#fee2e2', color: '#b91c1c' }
                            }}
                            onClick={() => handleDeleteItem(item.id)}
                            disabled={extracting}
                        />
                    </Stack>
                );
            }
        }
    ];

    return (
        <Dialog
            hidden={!isOpen}
            onDismiss={() => {
                handleReset();
                onDismiss();
            }}
            dialogContentProps={{
                type: DialogType.normal,
                title: 'Scan Surat Jalan via AI (Xiaomi MiMo Vision)',
                subText: 'Unggah foto scan lembar Surat Jalan untuk mengekstrak barang & mengisi data Container Goods secara otomatis.'
            }}
            modalProps={{
                isBlocking: true,
                styles: {
                    main: {
                        maxWidth: '1000px !important',
                        width: '94vw',
                        minWidth: '780px',
                        borderRadius: '12px'
                    }
                }
            }}
        >
            <Stack tokens={{ childrenGap: 16 }} styles={{ root: { marginTop: 10 } }}>
                {errorMessage && (
                    <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setErrorMessage(undefined)}>
                        {errorMessage}
                    </MessageBar>
                )}

                {/* Upload & Model Row */}
                <Stack horizontal tokens={{ childrenGap: 14 }} verticalAlign="end">
                    <Stack.Item grow={2}>
                        <Label required>Unggah Foto / Scan Surat Jalan (JPG, PNG, WEBP, PDF)</Label>
                        <input
                            type="file"
                            accept="image/*,application/pdf"
                            onChange={handleFileChange}
                            disabled={extracting}
                            style={{
                                display: 'block',
                                width: '100%',
                                padding: '8px 10px',
                                border: '1px dashed #94a3b8',
                                borderRadius: '6px',
                                backgroundColor: '#f8fafc',
                                cursor: 'pointer',
                                fontSize: '12px'
                            }}
                        />
                    </Stack.Item>

                    <Stack.Item grow={1}>
                        <Label>Model AI Vision</Label>
                        <Dropdown
                            selectedKey={selectedModel}
                            options={MODEL_OPTIONS}
                            onChange={(e, opt) => opt && setSelectedModel(opt.key as string)}
                            disabled={extracting}
                            styles={{ dropdown: { width: '100%' } }}
                        />
                    </Stack.Item>

                    <Stack.Item>
                        <PrimaryButton
                            text={extracting ? "Mengekstrak..." : "Scan via AI"}
                            iconProps={{ iconName: extracting ? undefined : 'DocumentSearch' }}
                            disabled={!file || extracting}
                            onClick={handleProcessOCR}
                            styles={{
                                root: {
                                    height: '34px',
                                    padding: '0 20px',
                                    backgroundColor: '#0284c7',
                                    borderColor: '#0284c7',
                                    fontWeight: 600
                                },
                                rootHovered: {
                                    backgroundColor: '#0369a1',
                                    borderColor: '#0369a1'
                                }
                            }}
                        />
                    </Stack.Item>
                </Stack>

                {/* Extracting Spinner */}
                {extracting && (
                    <Stack
                        horizontalAlign="center"
                        tokens={{ childrenGap: 10 }}
                        styles={{
                            root: {
                                padding: '30px 20px',
                                backgroundColor: '#f0f9ff',
                                borderRadius: '8px',
                                border: '1px solid #bae6fd'
                            }
                        }}
                    >
                        <Spinner size={SpinnerSize.large} label="Mengekstrak data Surat Jalan menggunakan Xiaomi MiMo AI..." />
                        <Text size="small" style={{ color: '#0369a1' }}>
                            AI sedang membaca No Surat Jalan, pengirim, penerima, dan tabel daftar barang...
                        </Text>
                    </Stack>
                )}

                {/* Two-Column Layout: Left (Preview Image) & Right (Extracted Table) */}
                {previewImages.length > 0 && !extracting && (
                    <Stack horizontal tokens={{ childrenGap: 16 }} styles={{ root: { minHeight: '340px' } }}>
                        {/* Left Column: Image Preview */}
                        <Stack
                            styles={{
                                root: {
                                    width: '240px',
                                    flexShrink: 0,
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '8px',
                                    padding: '8px',
                                    backgroundColor: '#f8fafc'
                                }
                            }}
                        >
                            <Text size="small" style={{ fontWeight: 600, color: '#475569', marginBottom: 6 }}>
                                Preview Surat Jalan
                            </Text>
                            <div
                                style={{
                                    width: '100%',
                                    height: '380px',
                                    overflowY: 'auto',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center'
                                }}
                            >
                                {previewImages.map((src, idx) => (
                                    <img
                                        key={idx}
                                        src={src}
                                        alt={`Surat Jalan Preview ${idx + 1}`}
                                        style={{
                                            maxWidth: '100%',
                                            marginBottom: 8,
                                            borderRadius: 4,
                                            border: '1px solid #cbd5e1',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                                        }}
                                    />
                                ))}
                            </div>
                        </Stack>

                        {/* Right Column: Extracted Surat Jalan & Items */}
                        <Stack grow={1} tokens={{ childrenGap: 10 }} styles={{ root: { minWidth: 0 } }}>
                            {suratJalanInfo && (
                                <Stack
                                    tokens={{ childrenGap: 6 }}
                                    styles={{
                                        root: {
                                            backgroundColor: '#f0fdf4',
                                            border: '1px solid #86efac',
                                            borderRadius: '6px',
                                            padding: '8px 12px',
                                            fontSize: '11px',
                                            color: '#166534'
                                        }
                                    }}
                                >
                                    <Stack horizontal wrap tokens={{ childrenGap: 12 }} verticalAlign="center">
                                        {suratJalanInfo.deliveryNoteNumber && (
                                            <span>
                                                📄 <strong>No SJ:</strong> {suratJalanInfo.deliveryNoteNumber}
                                            </span>
                                        )}
                                        {suratJalanInfo.date && (
                                            <span>
                                                📅 <strong>Tgl:</strong> {suratJalanInfo.date}
                                            </span>
                                        )}
                                        {suratJalanInfo.sender?.companyName && (
                                            <span>
                                                🏢 <strong>Pengirim (Supplier):</strong> {suratJalanInfo.sender.companyName}
                                                {suratJalanInfo.sender.contactPerson ? ` (${suratJalanInfo.sender.contactPerson})` : ''}
                                            </span>
                                        )}
                                        {suratJalanInfo.recipient?.companyName && (
                                            <span>
                                                📍 <strong>Tujuan:</strong> {suratJalanInfo.recipient.companyName}
                                                {suratJalanInfo.recipient.city ? ` - ${suratJalanInfo.recipient.city}` : ''}
                                            </span>
                                        )}
                                        {suratJalanInfo.vehicle?.licensePlate && (
                                            <span>
                                                🚛 <strong>Nopol:</strong> {suratJalanInfo.vehicle.licensePlate}
                                                {suratJalanInfo.vehicle.driverName ? ` (${suratJalanInfo.vehicle.driverName})` : ''}
                                            </span>
                                        )}
                                    </Stack>
                                </Stack>
                            )}

                            {extractedItems.length > 0 ? (
                                <>
                                    <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                                        <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                                            <Text style={{ fontWeight: 600, color: '#0f172a' }}>
                                                Hasil Ekstraksi AI ({extractedItems.length} barang)
                                            </Text>
                                            <span style={{ fontSize: '11px', color: '#0284c7', backgroundColor: '#e0f2fe', padding: '2px 8px', borderRadius: 12, fontWeight: 500 }}>
                                                ↕ Scroll ke bawah untuk melihat semua baris
                                            </span>
                                        </Stack>
                                        <DefaultButton
                                            text="Tambah Baris"
                                            iconProps={{ iconName: 'Plus' }}
                                            onClick={handleAddBlankRow}
                                        />
                                    </Stack>

                                    {/* Extracted Details List with disabled virtualization to guarantee 100% visible rows */}
                                    <div
                                        data-is-scrollable="true"
                                        style={{
                                            maxHeight: activeItem ? '220px' : '440px',
                                            overflowY: 'auto',
                                            overflowX: 'auto',
                                            border: '1px solid #cbd5e1',
                                            borderRadius: '6px',
                                            boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.04)',
                                            scrollbarWidth: 'thin',
                                            scrollbarColor: '#94a3b8 #f1f5f9'
                                        }}
                                    >
                                        <ShimmeredDetailsList
                                            items={extractedItems}
                                            columns={columns}
                                            selectionMode={SelectionMode.none}
                                            enableShimmer={false}
                                            onShouldVirtualize={() => false}
                                        />
                                    </div>

                                    {/* Expanded Per-Item Create Goods Form Card */}
                                    {activeItem && (
                                        <Stack
                                            tokens={{ childrenGap: 10 }}
                                            styles={{
                                                root: {
                                                    backgroundColor: '#ffffff',
                                                    border: '2px solid #0284c7',
                                                    borderRadius: '8px',
                                                    padding: '14px',
                                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
                                                }
                                            }}
                                        >
                                            <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                                                <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                                                    <span style={{ backgroundColor: '#0284c7', color: '#ffffff', borderRadius: '50%', width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700 }}>
                                                        {activeItem.itemNumber}
                                                    </span>
                                                    <Text style={{ fontWeight: 700, color: '#0f172a', fontSize: '14px' }}>
                                                        Detail Master Barang: {activeItem.jenisBarang || `Item #${activeItem.itemNumber}`}
                                                    </Text>
                                                </Stack>
                                                <IconButton
                                                    iconProps={{ iconName: 'Cancel' }}
                                                    title="Tutup Detail Form"
                                                    onClick={() => setExpandedItemId(undefined)}
                                                />
                                            </Stack>

                                            {/* Company & Customer Row */}
                                            <Stack horizontal tokens={{ childrenGap: 12 }}>
                                                <Stack.Item grow={1}>
                                                    <Label>Company</Label>
                                                    <TagPicker
                                                        itemLimit={1}
                                                        selectedItems={activeItem.company ? [{ key: activeItem.company.id, name: activeItem.company.name, metadata: activeItem.company } as any] : []}
                                                        onChange={(items) => {
                                                            const comp = items && items.length > 0 ? (items[0] as any).metadata : undefined;
                                                            handleItemChange(activeItem.id, 'company', comp);
                                                            if (!comp) handleItemChange(activeItem.id, 'customer', undefined);
                                                        }}
                                                        onResolveSuggestions={_onSearchCompanies}
                                                        onEmptyResolveSuggestions={() => _onSearchCompanies('')}
                                                    />
                                                </Stack.Item>

                                                <Stack.Item grow={1}>
                                                    <Label>Customer (PIC)</Label>
                                                    <NormalPeoplePicker
                                                        itemLimit={1}
                                                        selectedItems={activeItem.customer ? [{
                                                            id: activeItem.customer.id,
                                                            text: activeItem.customer.name,
                                                            secondaryText: activeItem.customer.email,
                                                            metadata: activeItem.customer
                                                        } as any] : []}
                                                        onChange={(items) => {
                                                            const cust = items && items.length > 0 ? (items[0] as any).metadata : undefined;
                                                            handleItemChange(activeItem.id, 'customer', cust);
                                                        }}
                                                        onResolveSuggestions={(kw) => _onSearchCustomers(activeItem.company?.id, kw)}
                                                        onEmptyResolveSuggestions={() => _onSearchCustomers(activeItem.company?.id, '')}
                                                        disabled={!activeItem.company}
                                                    />
                                                </Stack.Item>
                                            </Stack>

                                            {/* Dimensions Row */}
                                            <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign="end">
                                                <Stack.Item styles={{ root: { width: 90 } }}>
                                                    <Label>Satuan Dimensi</Label>
                                                    <Dropdown
                                                        selectedKey={activeItem.dimensionUnit || 'cm'}
                                                        options={DIMENSION_UNITS}
                                                        onChange={(e, opt) => opt && handleItemChange(activeItem.id, 'dimensionUnit', opt.key)}
                                                    />
                                                </Stack.Item>
                                                <Stack.Item grow={1}>
                                                    <Label>Panjang ({activeItem.dimensionUnit || 'cm'})</Label>
                                                    <TextField
                                                        value={activeItem.length || ''}
                                                        onChange={(e, val) => handleItemChange(activeItem.id, 'length', val || '')}
                                                        placeholder="0"
                                                    />
                                                </Stack.Item>
                                                <Stack.Item grow={1}>
                                                    <Label>Lebar ({activeItem.dimensionUnit || 'cm'})</Label>
                                                    <TextField
                                                        value={activeItem.width || ''}
                                                        onChange={(e, val) => handleItemChange(activeItem.id, 'width', val || '')}
                                                        placeholder="0"
                                                    />
                                                </Stack.Item>
                                                <Stack.Item grow={1}>
                                                    <Label>Tinggi ({activeItem.dimensionUnit || 'cm'})</Label>
                                                    <TextField
                                                        value={activeItem.height || ''}
                                                        onChange={(e, val) => handleItemChange(activeItem.id, 'height', val || '')}
                                                        placeholder="0"
                                                    />
                                                </Stack.Item>
                                                <Stack.Item styles={{ root: { width: 110, paddingBottom: 6 } }}>
                                                    <Text size="small" style={{ color: '#475569', fontWeight: 600 }}>
                                                        Volume (m3):
                                                    </Text>
                                                    <Text size="medium" style={{ fontWeight: 700, color: '#0284c7' }}>
                                                        {GeneralService.calculateVolume(activeItem.length, activeItem.width, activeItem.height, activeItem.dimensionUnit || 'cm')} m3
                                                    </Text>
                                                </Stack.Item>
                                            </Stack>

                                            {/* Warehouse & Location */}
                                            <Stack horizontal tokens={{ childrenGap: 12 }}>
                                                <Stack.Item grow={1}>
                                                    <Label>Warehouse Location</Label>
                                                    <TagPicker
                                                        itemLimit={1}
                                                        selectedItems={activeItem.warehouse ? [{ key: activeItem.warehouse.id, name: activeItem.warehouse.name, metadata: activeItem.warehouse } as any] : []}
                                                        onChange={(items) => {
                                                            const wh = items && items.length > 0 ? (items[0] as any).metadata : undefined;
                                                            handleItemChange(activeItem.id, 'warehouse', wh);
                                                        }}
                                                        onResolveSuggestions={_onSearchWarehouse}
                                                        onEmptyResolveSuggestions={() => _onSearchWarehouse('')}
                                                    />
                                                </Stack.Item>

                                                <Stack.Item grow={1}>
                                                    <Label>Location Details</Label>
                                                    <TextField
                                                        value={activeItem.locationDetails || ''}
                                                        onChange={(e, val) => handleItemChange(activeItem.id, 'locationDetails', val || '')}
                                                        placeholder="e.g. Rak B-12"
                                                    />
                                                </Stack.Item>
                                            </Stack>

                                            {/* Supplier, Delivery Date & Notes */}
                                            <Stack horizontal tokens={{ childrenGap: 12 }}>
                                                <Stack.Item grow={1}>
                                                    <Label>Supplier</Label>
                                                    <TextField
                                                        value={activeItem.supplier || ''}
                                                        onChange={(e, val) => handleItemChange(activeItem.id, 'supplier', val || '')}
                                                        placeholder="Nama Supplier"
                                                    />
                                                </Stack.Item>

                                                <Stack.Item grow={1}>
                                                    <Label>Delivery Date</Label>
                                                    <DatePicker
                                                        value={activeItem.deliveryDate ? new Date(activeItem.deliveryDate) : undefined}
                                                        onSelectDate={(date) => {
                                                            handleItemChange(activeItem.id, 'deliveryDate', date ? moment(date).toISOString() : undefined);
                                                        }}
                                                        placeholder="Pilih tanggal pengiriman"
                                                    />
                                                </Stack.Item>

                                                <Stack.Item grow={1}>
                                                    <Label>Notes</Label>
                                                    <TextField
                                                        value={activeItem.notes || ''}
                                                        onChange={(e, val) => handleItemChange(activeItem.id, 'notes', val || '')}
                                                        placeholder="Catatan barang..."
                                                    />
                                                </Stack.Item>
                                            </Stack>
                                        </Stack>
                                    )}
                                </>
                            ) : (
                                <Stack
                                    horizontalAlign="center"
                                    verticalAlign="center"
                                    styles={{
                                        root: {
                                            height: '280px',
                                            backgroundColor: '#f8fafc',
                                            borderRadius: '8px',
                                            border: '1px dashed #cbd5e1'
                                        }
                                    }}
                                >
                                    <Icon iconName="DocumentSearch" style={{ fontSize: 40, color: '#94a3b8', marginBottom: 12 }} />
                                    <Text style={{ fontWeight: 600, color: '#64748b' }}>
                                        Klik tombol "Scan via AI" di atas untuk memproses Surat Jalan.
                                    </Text>
                                    <Text size="small" style={{ color: '#94a3b8', marginTop: 4 }}>
                                        AI Xiaomi MiMo akan membaca daftar barang dan detail pengirim secara otomatis.
                                    </Text>
                                </Stack>
                            )}
                        </Stack>
                    </Stack>
                )}

                {/* Footer Buttons */}
                <Stack
                    horizontal
                    horizontalAlign="space-between"
                    verticalAlign="center"
                    styles={{
                        root: {
                            borderTop: '1px solid #e2e8f0',
                            paddingTop: '14px',
                            marginTop: '10px'
                        }
                    }}
                >
                    <DefaultButton
                        text="Batal"
                        onClick={() => {
                            handleReset();
                            onDismiss();
                        }}
                        disabled={extracting}
                    />

                    {extractedItems.length > 0 && (
                        <Stack horizontal tokens={{ childrenGap: 8 }}>
                            <DefaultButton
                                text="Reset Ulang"
                                onClick={handleReset}
                                disabled={extracting}
                            />
                            <PrimaryButton
                                text={`Terapkan ke Daftar Barang (${extractedItems.filter(it => it.jenisBarang && it.jenisBarang.trim() !== '').length} barang)`}
                                iconProps={{ iconName: 'CheckMark' }}
                                onClick={handleApplyAll}
                                disabled={extracting || extractedItems.filter(it => it.jenisBarang && it.jenisBarang.trim() !== '').length === 0}
                                styles={{
                                    root: {
                                        backgroundColor: '#0284c7',
                                        borderColor: '#0284c7',
                                        fontWeight: 600
                                    },
                                    rootHovered: {
                                        backgroundColor: '#0369a1',
                                        borderColor: '#0369a1'
                                    }
                                }}
                            />
                        </Stack>
                    )}
                </Stack>
            </Stack>
        </Dialog>
    );
};

export default SuratJalanGoodsScanModal;
